import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UiModule } from 'src/app/ui/ui.module';
import { ToolsService } from 'src/app/api/tools.service';
import { CustomerService } from 'src/app/api';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-merge-records',
  imports: [CommonModule, UiModule, ReactiveFormsModule],
  templateUrl: './merge-records.component.html',
  styleUrl: './merge-records.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MergeRecordsComponent {

  mergeForm: FormGroup;
  destGroupname = signal<string | null>(null);
  confirmationVisible = signal(false);
  loading = signal(false);
  mergeDone = signal(false);
  sourceGroupname = signal<string | null>(null);

  constructor(
    private fb: FormBuilder,
    private toolsService: ToolsService,
    private customerService: CustomerService,
    private messageService: MessageService
  ) {
    this.mergeForm = this.fb.group({
      sourceId: [null, [Validators.required, Validators.min(1)]],
      destId: [null, [Validators.required, Validators.pattern(/^\d+$/), Validators.min(1)]]
    });
  }

  selectAccounts() {
    if (this.mergeForm.valid) {
      this.loading.set(true);
      const { sourceId, destId } = this.mergeForm.value;

      this.customerService.getCustomer(sourceId).subscribe({
        next: (customer) => {
          this.sourceGroupname.set(customer.groupname ?? 'Not found');

          this.customerService.getCustomer(destId).subscribe({
            next: (destCustomer) => {
              this.destGroupname.set(destCustomer.groupname ?? 'Not found');
              this.confirmationVisible.set(true);
              this.loading.set(false);
              this.messageService.add({
                severity: 'success',
                summary: 'Customers found',
                detail: `${customer.groupname} will be merged into ${destCustomer.groupname}`
              });
            },
            error: () => {
              this.destGroupname.set('Not found');
              this.confirmationVisible.set(true);
              this.loading.set(false);
              this.messageService.add({
                severity: 'error',
                summary: 'Destination not found',
                detail: 'No destination customer with that ID'
              });
            }
          });
        },
        error: () => {
          this.sourceGroupname.set('Not found');
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Source not found',
            detail: 'No source customer with that ID'
          });
        }
      });
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid input',
        detail: 'Please enter valid account IDs'
      });
    }
  }

  confirmMerge() {
    if (this.mergeForm.valid) {
      this.loading.set(true);
      const { sourceId, destId } = this.mergeForm.value;
      this.toolsService.mergeRecord(sourceId, Number(destId)).subscribe({
        next: (result) => {
          this.destGroupname.set(result.destGroupname ?? 'Merged');
          this.mergeDone.set(true);
          this.loading.set(false);
          this.messageService.add({
            severity: 'success',
            summary: 'Merge complete',
            detail: `${this.sourceGroupname()} has been merged into ${this.destGroupname()}`
          });
          this.mergeForm.reset();
          this.sourceGroupname.set(null);
          this.destGroupname.set(null);
          this.confirmationVisible.set(false);
          this.mergeDone.set(false);
        },
        error: () => {
          this.destGroupname.set('Error merging');
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Merge failed',
            detail: 'An error occurred during merge'
          });
        }
      });
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid input',
        detail: 'Please enter valid account IDs'
      });
    }
  }
}
